import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { UseQueryResult } from '@tanstack/react-query';
import { toAppError } from '@mt/contracts';
import { Alert, Button, EmptyState, Skeleton, cn } from '@mt/ui';
import { AlertTriangle, ArrowLeft, ChevronDown, CloudOff, Info, Lock, RefreshCw } from 'lucide-react';

export function PageHeader({
  title,
  description,
  actions,
  back,
  eyebrow,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
  eyebrow?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:mb-8">
      {back && (
        <Link to={back.to} className="-ml-1 inline-flex min-h-11 w-fit items-center gap-1.5 rounded-lg px-1 text-sm font-semibold text-muted hover:text-ink">
          <ArrowLeft className="size-4" aria-hidden="true" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <p className="mb-1 text-sm font-semibold text-primary">{eyebrow}</p>}
          <h1 className="text-2xl font-bold sm:text-3xl">{title}</h1>
          {description && <p className="mt-1.5 max-w-2xl text-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** Container with the standard reading width and gutters. */
export function Page({ children, width = 'default', className }: { children: ReactNode; width?: 'default' | 'wide' | 'narrow'; className?: string }) {
  const max = width === 'wide' ? 'max-w-6xl' : width === 'narrow' ? 'max-w-2xl' : 'max-w-4xl';
  return <div className={cn('mx-auto w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8', max, className)}>{children}</div>;
}

export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const e = toAppError(error);
  const offline = e.code === 'NETWORK_ERROR';
  const denied = e.status === 403 || e.status === 404;
  return (
    <EmptyState
      icon={offline ? <CloudOff aria-hidden="true" /> : denied ? <Lock aria-hidden="true" /> : <AlertTriangle aria-hidden="true" />}
      title={title ?? (offline ? 'You’re offline' : denied ? 'Not available' : 'Something went wrong')}
      action={
        onRetry && !denied ? (
          <Button variant="secondary" icon={<RefreshCw className="size-4" aria-hidden="true" />} onClick={onRetry}>
            Try again
          </Button>
        ) : denied ? (
          <Link to="/" className="font-semibold text-primary underline-offset-4 hover:underline">
            Go to home
          </Link>
        ) : undefined
      }
    >
      {e.userMessage}
    </EmptyState>
  );
}

/**
 * Renders loading skeletons, a retryable error, or the data. Keeps showing
 * cached data during background refetches.
 */
export function QueryView<T>({
  query,
  skeleton,
  children,
  errorTitle,
}: {
  query: UseQueryResult<T>;
  skeleton?: ReactNode;
  children: (data: T) => ReactNode;
  errorTitle?: string;
}) {
  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} title={errorTitle} />;
  return <>{skeleton ?? <DefaultSkeleton />}</>;
}

export function DefaultSkeleton() {
  return (
    <div className="flex flex-col gap-4" role="status" aria-busy="true" aria-label="Loading">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-28 w-full" />
    </div>
  );
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div className="sticky top-0 z-40 safe-top">
      <Alert tone="brass" icon={<CloudOff className="size-4" aria-hidden="true" />} title="You’re offline">
        Saved answers will sync when you reconnect. Starting new practice needs a connection.
      </Alert>
    </div>
  );
}

/** Rules and fine print, folded away behind a small "How this works" toggle. */
export function HowItWorks({ title = 'How this works', children, className }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <details className={cn('group text-sm', className)}>
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1.5 rounded-lg font-semibold text-primary [&::-webkit-details-marker]:hidden">
        <Info className="size-4" aria-hidden="true" />
        {title}
        <ChevronDown className="size-4 transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="mt-1 max-w-2xl text-muted">{children}</div>
    </details>
  );
}

export function SectionTitle({ children, action, id }: { children: ReactNode; action?: ReactNode; id?: string }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <h2 id={id} className="text-lg font-bold">
        {children}
      </h2>
      {action}
    </div>
  );
}

export function FullScreenMessage({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-paper px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold">{title}</h1>
        {children && <div className="mt-2 text-muted">{children}</div>}
        {action && <div className="mt-6 flex justify-center">{action}</div>}
      </div>
    </main>
  );
}
